import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CartItem } from '../models/cart.model';
import { Product, PackagingOption } from '../models/product.model';
import { PosDataService } from './pos-data.service';
import { CurrencyService } from './currency.service';
import { CartPersistenceService } from './cart-persistence.service';
import { ReservationsService } from './reservations.service';
import { VentesDifferees } from './ventes-differees.service';
import { TerminalIdentityService } from '@stockflow/offline-core';
import { PosPricingRule, PosCustomer } from '../models/pos-catalog.model';
import { CartStateService } from './cart-state.service';
import { CartRestoreService } from './cart-restore.service';
import { CartCheckoutService } from './cart-checkout.service';
import { CartAutoAddService } from './cart-auto-add.service';
import { appliquerReponseCalcul } from './cart-pricing-update';

@Injectable({
    providedIn: 'root'
})
export class CartService {
    private posData = inject(PosDataService);
    private currencyService = inject(CurrencyService);
    private persistence = inject(CartPersistenceService);
    private destroyRef = inject(DestroyRef);
    /** Identite du poste, partagee avec /frontend. Voir shared/offline-core. */
    private terminalIdentity = inject(TerminalIdentityService);
    private ventes = inject(VentesDifferees);
    private reservations = inject(ReservationsService);

    // Collaborateurs : l'API publique reste ici, la mecanique vit a cote.
    private state = inject(CartStateService);
    private restauration = inject(CartRestoreService);
    private encaissement = inject(CartCheckoutService);
    private ajoutsOfferts = inject(CartAutoAddService);

    // Etat du panier et totaux : republies tels quels depuis CartStateService,
    // les composants les lisent toujours ici.
    readonly items = this.state.items;
    readonly discount = this.state.discount; // Global percentage 0-100
    readonly selectedCustomer = this.state.selectedCustomer;
    readonly taxes = this.state.taxes;
    readonly count = this.state.count;
    readonly subTotalCents = this.state.subTotalCents;
    readonly subTotal = this.state.subTotal;
    readonly discountAmountCents = this.state.discountAmountCents;
    readonly discountAmount = this.state.discountAmount;
    readonly taxableTotalCents = this.state.taxableTotalCents;
    readonly taxableTotal = this.state.taxableTotal;
    readonly totalTaxesCents = this.state.totalTaxesCents;
    readonly totalTaxes = this.state.totalTaxes;
    readonly totalCents = this.state.totalCents;
    readonly total = this.state.total;

    private refreshing = false;
    private refreshTimer: ReturnType<typeof setTimeout> | null = null;
    private syncing = false;

    // BOGO auto-added quantities tracking: "productId" or "productId:variantId" → qty
    private autoAddedQuantities = signal<Record<string, number>>({});

    /**
     * VTE-004 — le caissier n'avait aucun indicateur de ventes en attente.
     *
     * `FilePoussee`/`transmettreVentesDifferees()` existaient et fonctionnaient
     * (drainage au demarrage et au retour reseau, voir le constructeur), mais
     * rien ne rendait `ventesDifferees()` observable par un template : une
     * vente pouvait rester en file des heures sans que l'ecran ne le montre.
     *
     * Signal plutot que la methode async existante : un composant peut le
     * lire directement, sans sondage ni abonnement a gerer.
     */
    readonly ventesEnAttente = this.ventes.enAttente;

    /** Ventes d'autres utilisateurs gardees sur ce poste (voir `VentesDifferees`). */
    readonly ventesDAutres = this.ventes.dAutres;

    private rafraichirVentesEnAttente(): Promise<void> {
        return this.ventes.rafraichir();
    }

    readonly currencySymbol = computed(() => this.posData.currency().symbol);
    readonly displayTotal = computed(() => this.currencyService.convert(this.total()));
    readonly displaySymbol = computed(() => this.currencyService.activeCurrency().symbol);
    readonly displayCurrencyCode = computed(() => this.currencyService.activeCurrency().code);

    constructor() {
        // Restore cart from IndexedDB on startup
        this.restauration.demarrerRestauration(() => this.refreshPricing());

        // Vidange de la file au demarrage et au retour du reseau.
        //
        // Sans declencheur, les ventes differees s'accumuleraient sans jamais
        // etre transmises — exactement le defaut constate sur la file de
        // retraitement cote serveur, ou le moteur existait sans que rien ne
        // depile.
        //
        // Les echecs sont ignores ici : la file les conserve et le prochain
        // declenchement reprendra. Jeter depuis un constructeur ne servirait
        // qu'a casser le demarrage.
        if (typeof window !== 'undefined') {
            // Lu tout de suite, avant meme la tentative de drainage : sans
            // ca, une file deja remplie au demarrage afficherait 0 le temps
            // que le premier essai de transmission se resolve.
            void this.rafraichirVentesEnAttente();
            void this.transmettreVentesDifferees().catch(() => undefined);
            window.addEventListener('online', () => {
                void this.transmettreVentesDifferees().catch(() => undefined);
            });
        }

        // Auto-persist on any cart state change
        this.restauration.persisterAChaqueChangement();
    }

    /**
     * Ajoute au panier — APRES avoir reserve le stock.
     *
     * L'appel BLOQUE sur la reservation, et c'est deliberé. L'article n'entre
     * au panier que s'il est reellement vendable : refuser tout de suite, avec
     * le chiffre disponible, vaut mieux que faire disparaitre une ligne sous
     * les yeux du vendeur en pleine transaction.
     *
     * Hors ligne, la reservation est sautee et l'ajout passe : refuser de
     * vendre parce qu'Internet est tombe arreterait le commerce. Voir
     * ReservationsService.
     *
     * @returns `ajoute: false` avec un message quand le stock ne suffit pas.
     */
    async addToCart(
        product: Product,
        variant: string,
        packaging: PackagingOption,
        qty: number,
    ): Promise<{ ajoute: boolean; message?: string }> {
        const reservation = await this.reserverPour(product, variant, qty);
        if (!reservation.pris) {
            return { ajoute: false, message: reservation.message };
        }

        this.ajouterAuPanier(product, variant, packaging, qty);
        return { ajoute: true };
    }

    /**
     * Reserve la quantite demandee pour ce poste.
     *
     * La variante est stockee dans le panier sous forme de LIBELLE ; le
     * serveur attend un identifiant. La correspondance vit dans
     * `product.stock.byVariant`, seule source qui porte les deux.
     */
    private async reserverPour(product: Product, variant: string, qty: number) {
        // Un service n'a pas de stock a reserver : le serveur le refuserait de toute facon.
        if (product.productKind === 'SERVICE') return { pris: true };

        const variantId =
            product.stock?.byVariant?.find((v) => v.variantLabel === variant)
                ?.variantId ?? null;
        /**
         * Le choix retenu a l'ouverture du POS, lu directement : interroger la
         * liste des entrepots a chaque ajout au panier mettrait un aller-retour
         * reseau sur le chemin le plus chaud de la caisse, et la rendrait
         * inutilisable hors ligne.
         *
         * Le repli sur l'identifiant 1 subsiste, mais il n'est plus atteint en
         * usage normal : `sales.component` resout desormais un entrepot REEL de
         * l'entreprise a l'ouverture du POS et le memorise. Auparavant ce repli
         * etait la regle des qu'aucune session de caisse n'avait ete ouverte —
         * et l'entrepot 1, partage entre toutes les entreprises, faisait
         * reserver sur un stock possiblement etranger.
         */
        const stocke = localStorage.getItem('pos_warehouse_id');
        const warehouseId = stocke ? Number(stocke) : 1;

        return this.reservations.reserver({
            productId: product.id,
            warehouseId,
            variantId,
            quantity: qty,
            ownerId: this.identifiantPanier(),
        });
    }

    /**
     * Proprietaire des reservations : le POSTE, pas l'utilisateur.
     *
     * Un panier appartient a une caisse. Si le vendeur change sans vider le
     * panier, les reservations doivent rester attachees a la meme caisse —
     * sinon elles seraient orphelines et bloqueraient du stock jusqu'a
     * expiration.
     */
    private identifiantPanier(): string {
        return this.terminalIdentity.code() ?? 'poste-inconnu';
    }

    /** Libere ce que tient ce poste. Encaissement ou abandon du panier. */
    async libererReservations(): Promise<void> {
        await this.reservations.libererPanier(this.identifiantPanier());
    }

    private ajouterAuPanier(product: Product, variant: string, packaging: PackagingOption, qty: number) {
        const cartId = `${product.id}-${variant}-${packaging.id}`;
        const price = packaging.salePrice || (product.price * packaging.multiplier);

        this.items.update(items => {
            const existing = items.find(i => i.cartId === cartId);
            if (existing) {
                // lineTotalCents devient périmé dès que la quantité bouge :
                // on l'invalide jusqu'à la prochaine réponse de /pos/calculate.
                return items.map(i => i.cartId === cartId
                    ? { ...i, qty: i.qty + qty, lineTotalCents: undefined }
                    : i);
            }
            return [...items, {
                cartId,
                id: product.id,
                name: product.name,
                price,
                image: product.image,
                category: product.category,
                variants: product.variants,
                sku: product.sku,
                commonName: product.commonName,
                imageUrl: product.imageUrl,
                familyId: product.familyId,
                stock: product.stock,
                isComposite: product.isComposite,
                recipeComponents: product.recipeComponents,
                variant,
                packaging,
                unitPrice: product.price,
                qty,
                applicableRules: [],
                selectedRule: null,
            }];
        });

        this.refreshPricing();
    }

    removeFromCart(cartId: string) {
        this.items.update(items => items.filter(i => i.cartId !== cartId));
        if (this.items().length === 0) {
            this.taxes.set([]);
            this.autoAddedQuantities.set({});
        } else {
            this.refreshPricing();
        }
    }

    updateQty(cartId: string, quantity: number) {
        if (quantity <= 0) {
            this.removeFromCart(cartId);
            return;
        }
        this.items.update(items => {
            return items.map(item => {
                if (item.cartId === cartId) {
                    // Invalide le total exact : il correspond à l'ancienne quantité.
                    return { ...item, qty: quantity, lineTotalCents: undefined };
                }
                return item;
            });
        });
        this.refreshPricing();
    }

    /** Définit la remise globale en pourcentage (0-100). */
    setDiscount(percent: number): void {
        if (percent < 0 || percent > 100) {
            throw new RangeError(`Remise invalide : ${percent}%. Doit être entre 0 et 100.`);
        }
        this.discount.set(percent);
    }

    /** Apply a pricing rule to a specific cart item (or null to clear) */
    applyRule(item: CartItem, rule: PosPricingRule | null) {
        this.items.update(items => items.map(i => {
            if (i.cartId === item.cartId) {
                const newPrice = this.calculateItemPrice(i, rule);
                // calculateItemPrice ne produit qu'une estimation locale pour un
                // retour visuel immédiat. Le total exact est réattribué par
                // /pos/calculate juste après (refreshPricing ci-dessous).
                return { ...i, selectedRule: rule, price: newPrice, lineTotalCents: undefined };
            }
            return i;
        }));
        this.refreshPricing();
    }

    /** Calculate item price based on selected rule (client-side for immediate UI feedback) */
    private calculateItemPrice(item: CartItem, rule: PosPricingRule | null): number {
        const basePrice = item.packaging.salePrice || (item.unitPrice * item.packaging.multiplier);
        if (!rule) return basePrice;

        const strategy = rule.discountStrategy || 'PERCENTAGE';

        if (strategy === 'PERCENTAGE') {
            return Number((basePrice * (1 - (rule.discountPercentage || 0) / 100)).toFixed(2));
        } else if (strategy === 'FIXED_AMOUNT') {
            return Number(Math.max(0, basePrice - (rule.discountAmount || 0)).toFixed(2));
        } else if (strategy === 'FIXED_PRICE') {
            return rule.discountAmount || basePrice;
        } else if (strategy === 'BOGO') {
            const buyQty = rule.buyQuantity || 1;
            const getQty = rule.getQuantity || 1;
            const totalQty = item.qty;
            const cycleSize = buyQty + getQty;
            const cycles = Math.floor(totalQty / cycleSize);
            const remainder = totalQty % cycleSize;
            const paidItems = (cycles * buyQty) + Math.min(remainder, buyQty);
            const totalPrice = paidItems * basePrice;
            // Return effective unit price (weighted average)
            return totalQty > 0 ? Number((totalPrice / totalQty).toFixed(2)) : basePrice;
        }
        return basePrice;
    }

    /** Call backend BFF to get applicable rules + recalculate prices (debounced 300ms) */
    refreshPricing() {
        // Debounce: prevents API spam when user rapidly changes quantities
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
        this.refreshTimer = setTimeout(() => this.doRefreshPricing(), 300);
    }

    private doRefreshPricing() {
        if (this.refreshing || this.syncing) return;

        const currentCart = this.items();
        if (currentCart.length === 0) return;

        this.refreshing = true;

        const customer = this.selectedCustomer();
        const payload = {
            customerId: customer?.id || 0,
            items: currentCart.map(i => ({
                productId: i.id,
                familyId: i.familyId,
                basePrice: i.packaging.salePrice || (i.unitPrice * i.packaging.multiplier),
                quantity: i.qty,
                variantId: i.packaging.variantId || undefined,
                packagingUnitId: i.packaging.unitId,
                selectedRuleId: i.selectedRule ? i.selectedRule.id : undefined
            })),
        };

        this.posData.calculate(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: ({ pricing: result, applicableRules: rulesMap, taxes: backendTaxes }) => {
                // 1. Sync BOGO auto-added items
                const autoAddedChanged = this.syncAutoAddedItems(result.benefitEntitlements || {});

                if (autoAddedChanged) {
                    this.refreshing = false;
                    // Cart changed by auto-add, re-run pricing
                    setTimeout(() => this.doRefreshPricing(), 0);
                    return;
                }

                // 2. Update prices + applicable rules
                this.items.update(items => appliquerReponseCalcul(items, result.items, rulesMap));

                // Taxes calculées par le backend BFF
                this.taxes.set(backendTaxes || []);

                this.refreshing = false;
            },
            error: (err) => {
                console.error('[CartService] refreshPricing failed:', err);
                this.refreshing = false;
            }
        });
    }

    /**
     * Sync BOGO auto-added items (cross-product BOGO).
     *
     * `syncing` reste ICI : `doRefreshPricing` le lit pour ne pas se relancer
     * pendant la synchronisation. Le calcul vit dans `CartAutoAddService`, qui
     * ajoute par le rappel `addToCart` de cette façade.
     */
    private syncAutoAddedItems(entitlements: Record<string, number>): boolean {
        this.syncing = true;
        const cartChanged = this.ajoutsOfferts.synchroniser(entitlements, {
            autoAddedQuantities: this.autoAddedQuantities,
            ajouter: (product, variant, packaging, qty) =>
                this.addToCart(product, variant, packaging, qty),
        });
        this.syncing = false;
        return cartChanged;
    }

    /** Get a display label for a pricing rule */
    getDiscountLabel(rule: PosPricingRule): string {
        const strategy = rule.discountStrategy || 'PERCENTAGE';
        if (strategy === 'PERCENTAGE') return `-${Number(rule.discountPercentage || 0).toFixed(2)}%`;
        if (strategy === 'FIXED_AMOUNT') return `-$${Number(rule.discountAmount || 0).toFixed(2)}`;
        if (strategy === 'FIXED_PRICE') return `$${Number(rule.discountAmount || 0).toFixed(2)}`;
        if (strategy === 'BOGO') return `${rule.buyQuantity || 1}+${rule.getQuantity || 1}`;
        return '';
    }

    setCustomer(customer: PosCustomer | null) {
        this.selectedCustomer.set(customer);
        // Re-calculate pricing when customer changes (client type may affect rules)
        if (this.items().length > 0) {
            this.refreshPricing();
        }
    }

    /**
     * Finalise la vente : cree commande, facture, paiement cote backend.
     * Une vente qui part ne se coupe pas : le verrou du poste attend qu'elle ait fini.
     * Voir `CartCheckoutService`.
     */
    checkout(...a: Parameters<CartCheckoutService['checkout']>) {
        return this.encaissement.checkout(...a);
    }

    /**
     * Vide la file des ventes differees.
     *
     * A appeler au retour du reseau et au demarrage : une vente en file n'a pas
     * encore ete encaissee cote serveur, et rien ne la transmettra d'elle-meme.
     *
     * Les refus metier sont retires de la file — ils se reproduiraient a
     * l'identique — mais REMONTES dans le bilan. C'est l'inverse de l'abandon
     * silencieux de l'ecart Y9.
     */
    transmettreVentesDifferees() {
        return this.ventes.transmettre();
    }

    /** Nombre de ventes en attente de transmission. */
    async ventesDifferees(): Promise<number> {
        await this.ventes.rafraichir();
        return this.ventes.enAttente();
    }

    /**
     * Vide le panier — et LIBERE les reservations qu'il tenait.
     *
     * Point unique : la vente aboutie comme l'abandon passent ici. Sans cette
     * liberation, chaque vente laisserait du stock bloque jusqu'a l'expiration
     * de la reservation, trente minutes plus tard. Rien ne casserait, mais le
     * disponible affiche serait faux tout ce temps — exactement le probleme
     * qu'on cherche a corriger.
     *
     * L'appel n'est PAS attendu : une liberation qui echoue ne doit pas
     * empecher le panier de se vider sous les yeux du vendeur. Les
     * reservations expirent d'elles-memes, le pire cas est une attente.
     */
    clearCart() {
        void this.reservations.libererPanier(this.identifiantPanier());
        this.items.set([]);
        this.discount.set(0);
        this.selectedCustomer.set(null);
        this.taxes.set([]);
        this.autoAddedQuantities.set({});
        this.persistence.clear();
    }
}
